import React, { useState } from 'react'
import { differenceInHours, startOfYear } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { calculatorVar, projectVar } from '../../../apollo/reactive-vars'
import {
  BuildingFragment,
  DemandType,
  ProviderFragment,
} from '../../../generated/graphql'
import { EnergyType } from '../../../calculators/EnergyFlow'
import { ConsumptionType } from '../../system-container/energy-system/usage-matrix-util'
import { Section } from '../../common/Section'
import {
  FormValues,
  UsageMatrixControls,
} from '../../system-container/energy-system/UsageMatrixControls'
import { UsageMatrixTable } from '../../system-container/energy-system/UsageMatrixTable'
import { SpecialDemand } from '../../../calculators/special-demand-ids'
import { createHourSeriesNameFormatter } from '../../../hour-series/name-formatter'

type Props = {
  building: BuildingFragment
  providers: ProviderFragment[]
  energyType: EnergyType
  visibleDemandType: DemandType
  onEditAllocation: (
    provider: ProviderFragment,
    demandId: string,
    maxPortion: number,
  ) => void
  editDisabled: boolean
}

export function BuildingUsageMatrix({
  building,
  providers,
  energyType,
  visibleDemandType,
  onEditAllocation,
  editDisabled,
}: Props): React.ReactElement {
  const project = useReactiveVar(projectVar)
  const calculator = useReactiveVar(calculatorVar)
  const { t } = useTranslation()

  const startOfYearDate = startOfYear(
    project?.calendarYear
      ? new Date(`${project.calendarYear}-01-01 00:00:00`)
      : new Date(),
  )
  const [controls, setControls] = useState<FormValues>({
    consumptionType: ConsumptionType.TotalEnergy,
    date: startOfYearDate,
  })
  const hourOfYear = differenceInHours(controls.date, startOfYearDate)

  const buildingCalculator = calculator?.findBuilding(building.id)

  if (!project || !buildingCalculator) {
    return <div />
  }

  const visibleDemands = building.demands.filter(
    (demand) => demand.type === visibleDemandType,
  )

  const formatHourSeriesName = createHourSeriesNameFormatter(
    project,
    building.parts,
  )

  const demands = visibleDemands.map((demand) => ({
    id: demand.id,
    name: formatHourSeriesName(demand.hourSeries),
    isToggleable: !editDisabled,
    isEditable: !editDisabled,
  }))

  const demandsFromOtherProviders =
    energyType === EnergyType.Electricity
      ? [
          {
            id: SpecialDemand.OtherProvidersElectricity,
            name: t(`localGrid.tabs.energySystem.otherProviders`),
            isToggleable: !editDisabled,
            isEditable: false,
          },
        ]
      : []

  return (
    <Section title={t('building.tabs.energySystem.usageMatrix')}>
      <UsageMatrixControls
        startOfYearDate={startOfYearDate}
        defaultValues={controls}
        onSubmit={(values) => setControls(values)}
      />

      <UsageMatrixTable
        energyType={energyType}
        consumptionType={controls.consumptionType}
        hourOfYear={hourOfYear}
        calculator={buildingCalculator}
        providers={providers}
        demands={[...demands, ...demandsFromOtherProviders]}
        onEditAllocation={onEditAllocation}
      />
    </Section>
  )
}
